<template>
  <div class="h-100 payout-form">
    <div>
      <b-row class="heading-container">
        <go-back />
        <h4 class="ml-2">
          {{ pageTitle }}
        </h4>
      </b-row>
      <tabs-layout
        :tabs="sections"
        v-model="tabIndex"
        @change="handleTabChange"
      >
        <!-- Tab Title -->
        <template v-slot:tabTitle="{ tab }">
          {{ tab.title }}
        </template>
        <template v-slot:content="{ tab }">
          <component
            :ref="tab.ref"
            :is="tab.component"
            :payout-id="payoutId"
            :payout-data="payoutMode === payoutModes.CREATE ? payoutData : fetchedPayoutData"
            :payout-mode="payoutMode"
            :can-edit="canEdit"
            @get-data="getData"
            @reset="handleResetForm"
            @change-payout-mode="handlePayoutMode"
            @update-payout-data="updatePayoutData"
            @payout-status-update="handlePayoutStatusUpdate"
            v-bind="tab.extra"
          ></component>
        </template>
      </tabs-layout>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex"
import { payoutModes, payoutStatuses, payoutTabs } from "@/constants"
import DetailsSection from "@/pages/Payouts/CreateSections/DetailsSection"
import { Pages } from "@/utils/pages"
import PendingQueueSection from "@/pages/Payouts/ViewSections/PendingQueueSection"
import ReportSection from "@/pages/Payouts/CreateSections/ReportSection"
import ScopeSection from "@/pages/Payouts/CreateSections/ScopeSection"
import StatementsSection from "@/pages/Payouts/ViewSections/StatementsSection"
import TransactionsSection from "@/pages/Payouts/ViewSections/TransactionsSection"
import { capitalize } from "lodash"

export default {
  name: "CreatePayoutsPage",
  component: {
    DetailsSection,
    PendingQueueSection,
    ReportSection,
    ScopeSection,
    TransactionsSection,
  },
  props: {
    payoutId: String,
  },
  data () {
    return {
      tabIndex: 0,
      payoutMode: payoutModes.CREATE,
      payoutModes,
      payoutStatuses,
      payoutData: {
        transactionBatchId: "",
        payoutId: "",
        createdBy: "",
        managedBy: "",
        description: "",
        estimatedCompletionDate: null,
        completionDate: "",
        distributionsCount: "",
        tariffsIncludedInPayout: "",
        status: "not_started", 
        scope: {
          college: null,
          minThreshold: "25",
          distributions: {
            ids: [],
            select: "include",
          },
          societies: {
            ids: [],
            select: "include",
          },
          copyrights: {
            codes: [],
            select: "include",
          },
          includeCloseoutTransactions: null,
        }
      },
      selectedSociety: {
        except: false,
        values: [],
      },
      selectedCopyrights: {
        except: false,
        values: [],
      },
      selectedDistribution: {
        except: false,
        values: [],
      },
      fetchedPayoutData: {},
      duplicateFetchedPayoutData: {},
      pageTitle: "",
    }
  },
  methods : {
    ...mapActions("alert", ["success", "error"]),

    handleTabChange ({ index, tab }) {
      this.tabIndex = index
      if (this.payoutData.status === payoutStatuses.NOT_STARTED && this.payoutMode === payoutModes.CREATE) {
        if (tab.type === payoutTabs.DETAILS) {
          this.pageTitle = capitalize(this.$t("create_payout"))
        } else if (tab.type === payoutTabs.SCOPE) {
          this.pageTitle = capitalize(this.$t("set_scope"))
        } else {
          this.pageTitle = this.fetchedPayoutData.description
        }
      } else if (this.fetchedPayoutData.status === payoutStatuses.NOT_STARTED) {
        if (tab.type === payoutTabs.SCOPE) {
          this.pageTitle = capitalize(this.$t("set_scope"))
        } else {
          this.pageTitle = this.fetchedPayoutData.description
        }
      }
    },

    async getData (payoutId) {
      const response = await this.$api.payouts.getPayout((payoutId))
      await this.updatePayoutData(response.data)
    },
    updatePayoutData (newVal) {
      this.fetchedPayoutData = newVal
      if (!this.fetchedPayoutData.scope) {
        this.fetchedPayoutData.scope = this.payoutData.scope
      }
      this.duplicateFetchedPayoutData = JSON.parse(JSON.stringify(this.fetchedPayoutData))
    },
    handleResetForm () {
      this.fetchedPayoutData = JSON.parse(JSON.stringify(this.duplicateFetchedPayoutData))
      this.payoutMode = payoutModes.VIEW
    },
    handlePayoutMode (value) {
      this.payoutMode = value
    },
    handlePayoutStatusUpdate (status) {
      this.$set(this.fetchedPayoutData, "status", status)
    },
    viewTransactionsRule () {
      if (this.fetchedPayoutData.status) {
        return this.fetchedPayoutData.status === payoutStatuses.NOT_STARTED ? true : false
      } else {
        return this.payoutData.status === payoutStatuses.NOT_STARTED ? true : false
      }
    },
  },
  computed: {
    ...mapState("user", ["permissions"]),
    sections () {
      return [
        {
          ref: "details",
          title: capitalize(this.$t("details")),
          component: DetailsSection,
          visible: true,
          type: payoutTabs.DETAILS,
          disabled: false
        },
        {
          ref: "scope",
          title: capitalize(this.$t("scope")),
          component: ScopeSection,
          visible: true,
          type: payoutTabs.SCOPE,
          disabled: this.fetchedPayoutData.id ? false : true,
          extra: {
            selectedSociety: this.selectedSociety,
            selectedDistribution: this.selectedDistribution,
            selectedCopyrights: this.selectedCopyrights,
          }
        },
        {
          ref: "transactions",
          title: capitalize(this.$t("transactions")),
          component: TransactionsSection,
          visible: true,
          type: payoutTabs.TRANSACTIONS,
          extra: {
            isSubTab: true,
            payoutData: this.fetchedPayoutData,
          },
          disabled: this.viewTransactionsRule()
        },
        {
          ref: "pendingQueue",
          title: capitalize(this.$t("pending_queue")),
          component: PendingQueueSection,
          visible: this.permissions.pages[Pages.TransactionsRemovalList].canRead,
          type: payoutTabs.PENDING_QUEUE,
          disabled: this.viewTransactionsRule(),
        },
        {
          ref: "statements",
          title: capitalize(this.$t("payment_statements")),
          component: StatementsSection,
          visible: true,
          type: payoutTabs.STATEMENTS,
          extra: {
            isSubTab: true,
            payoutData: this.fetchedPayoutData,
          },
          disabled: this.viewTransactionsRule(),
        },
        {
          ref: "report",
          title: capitalize(this.$t("report")),
          component: ReportSection,
          visible: false,       // until reports are ready
          type: payoutTabs.REPORT,
        },
      ]
    },
    canEdit () {
      return !(
        this.fetchedPayoutData.status === payoutStatuses.COMPLETED
        || this.fetchedPayoutData.status === payoutStatuses.CANCELLED
      )
    },
  },
  watch: {
    fetchedPayoutData (newVal) {
      this.pageTitle = newVal && newVal.description
    }
  },
  mounted () {
    if (this.payoutId) {
      this.getData(this.payoutId)
      this.payoutMode = payoutModes.VIEW
    } else {
      this.pageTitle = capitalize(this.$t("create_payout"))
    }
  },
}
</script>

<style lang="css" scoped> 
.payout-form {

  ::v-deep .tab-content {
    background-color: transparent !important;
    padding: 10px;
  }

  ::v-deep .fixed-footer {
    position: fixed;
    min-height: 30px;
    bottom: 40px;
    left: 0;
    width: calc(100% - 15px);
  }

  ::v-deep .view-values {
    margin: auto 0;
  }

  ::v-deep .no-title {
    padding-top: 0;
  }
}

</style>